<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import ProjectTile from './ProjectTile.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'manage'])

const store = useStore()
const router = useRouter()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const allowance = computed(() => store.getters['Projects/projectAllowance'])
const projects = computed(() => store.getters['Projects/projects'])

const intro = computed(() => {
  const limit = Number(allowance.value.limit) || 0
  const plan = allowance.value.planName ? `The ${allowance.value.planName} plan` : 'Your plan'
  return limit === 1
    ? `${plan} includes 1 workspace, and it is in use.`
    : `${plan} includes ${limit} workspaces, and all of them are in use.`
})

const upgrade = () => {
  isOpen.value = false
  router.push({ path: '/subscribe' })
}

const manage = () => {
  isOpen.value = false
  emit('manage')
}
</script>

<template>
  <ModalBox v-model="isOpen" title="Workspace limit reached" size="sm">
    <div class="px-4 sm:px-6 pb-2">
      <p class="text-sm text-surface-9 mb-4">{{ intro }}</p>

      <ul class="flex flex-col gap-1.5 mb-5 pl-0 list-none">
        <li
          v-for="project in projects"
          :key="project.id"
          class="flex items-center gap-2.5 rounded-md bg-surface-1 px-3 py-2 text-tiny text-surface-text-1"
        >
          <ProjectTile :name="project.name" :color="project.color" size="size-6" textClass="text-[10px]" />
          <span class="truncate">{{ project.name }}</span>
        </li>
      </ul>

      <p class="text-tiny text-surface-8 mb-0">
        To add another, upgrade for more workspaces — or remove one you no longer need. Streams,
        storage and bandwidth stay on your single subscription either way.
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 px-4 sm:px-6 py-4 border-t border-surface-3">
        <FormButton type="secondary" size="md" label="Manage workspaces" class="justify-center" @click="manage" />
        <FormButton type="main" size="md" label="See plans" class="justify-center" @click="upgrade" />
      </div>
    </template>
  </ModalBox>
</template>
